/**
 * Read-only "fiche" card of the extranet (contract details, insured, claim,
 * vehicle, vehicle contract): a title + label/value pairs rendered by the
 * design-system `InfoList` from a declarative field list with stable keys.
 *
 * Configurable-ready:
 * - `fields` is the declarative config (stable `key`, French `label`,
 *   `tooltip`, `order`, `visible`, `wide`, optional status `badge`); `values`
 *   is the data, keyed by the same keys (pre-formatted strings / numbers;
 *   `null` renders an empty value but keeps its slot);
 * - `_layoutOverrides` (injected by `withComponentRules`) is threaded into the
 *   `InfoList`s (`items` / `fields` bucket: rename / hide / reorder /
 *   tooltip), the title (`setTitle`) and the action buttons (`actions`
 *   bucket: `setActionLabel` / `hideAction`, keyed by action `key`);
 * - the base config is published with `usePublishLayoutConfig(componentId)`
 *   so the admin editor lists every item, the title and the actions.
 *
 * NOT registered by itself: each domain registers a usage under its own id
 * (`extranet.contractDetailsCard`, …) — see `bindCardProps` and the domain
 * `register.ts` files. `values === null` (VEOS returned no record) shows the
 * empty state « aucune donnée disponible ».
 */

import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';

import { Button } from '@components/base/actions';
import { InfoList, type InfoItemDescriptor } from '@components/base/display';
import { Card, VStack } from '@components/base/layout';
import type { ActionsConfig } from '@components/integrated/entity-form';
import {
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
  type LayoutOverrides,
} from '@components/shared/configurable';

import { useSpaLink } from '../useSpaLink';
import { CardBodyState } from './CardBodyState';
import { CardTitleRow } from './CardTitleRow';
import { StatusBadge } from './StatusBadge';
import type { StatusBadgeConfig } from './statusBadgeConfig';

/** One label/value pair of the card (declarative config, stable `key`). */
export interface EntityInfoField {
  /** Stable key — the rule contract and the key of `values`. */
  key: string;
  /** Display label (overridable). */
  label: string;
  tooltip?: string;
  /** Sort order (lower first); defaults to the declaration index. */
  order?: number;
  /** `false` ⇒ hidden at baseline (revealable by a rule). */
  visible?: boolean;
  /** Long text: rendered full width below the grid (description, dommages…). */
  wide?: boolean;
  /** Render the value as a status badge. */
  badge?: StatusBadgeConfig;
}

/** A navigation button of the card (e.g. « Voir le contrat »). */
export interface EntityInfoCardAction {
  /** Stable action key (rule contract of the `actions` bucket). */
  key: string;
  label: string;
  /** Absolute path the button opens. */
  to: string;
  variant?: 'primary' | 'secondary' | 'ghost';
}

/** A field value: pre-formatted text or number; `null` ⇒ empty value. */
export type EntityInfoValue = string | number | null | undefined;

export interface EntityInfoCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base card title (overridable with `setTitle`); omitted ⇒ no header. */
  title?: string;
  icon?: LucideIcon;
  fields: readonly EntityInfoField[];
  /** Values keyed by field key; `null` ⇒ VEOS returned no record (empty state). */
  values: Readonly<Record<string, EntityInfoValue>> | null;
  loading?: boolean;
  error?: string | null;
  /** Grid columns of the short fields. Default 2. */
  columns?: 1 | 2;
  actions?: readonly EntityInfoCardAction[];
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

/** Handler name published for the navigation actions (documentation only). */
const NAVIGATE_ACTION_HANDLER = 'extranet-navigate';

function renderValue(field: EntityInfoField, value: EntityInfoValue) {
  if (value === null || value === undefined || value === '') return null;
  if (field.badge) return <StatusBadge value={value} config={field.badge} />;
  return String(value);
}

export function EntityInfoCard({
  componentId,
  title,
  icon,
  fields,
  values,
  loading = false,
  error = null,
  columns = 2,
  actions,
  _layoutOverrides,
}: Readonly<EntityInfoCardProps>) {
  const spaLink = useSpaLink();

  // Published base config: the override-able surface of every item.
  const publishedItems = useMemo(
    () =>
      fields.map((field, index) => ({
        key: field.key,
        label: field.label,
        order: field.order ?? index,
        ...(field.visible === undefined ? {} : { visible: field.visible }),
        ...(field.tooltip === undefined ? {} : { tooltip: field.tooltip }),
      })),
    [fields],
  );
  const publishedActions = useMemo<ActionsConfig | undefined>(
    () =>
      actions && actions.length > 0
        ? {
            custom: actions.map((action) => ({
              id: action.key,
              label: action.label,
              handler: NAVIGATE_ACTION_HANDLER,
              variant: action.variant ?? 'secondary',
            })),
          }
        : undefined,
    [actions],
  );
  // `title ?? ''`: an InfoList host only offers the title slot in the editor
  // when a title is published — the tab cards have none at baseline but a
  // `setTitle` rule must still be authorable.
  usePublishLayoutConfig(componentId, {
    ...buildPublishedConfigFromInfoItems(publishedItems, title ?? ''),
    ...(publishedActions ? { actions: publishedActions } : {}),
  });

  const { shortItems, wideItems } = useMemo(() => {
    const short: InfoItemDescriptor[] = [];
    const wide: InfoItemDescriptor[] = [];
    fields.forEach((field, index) => {
      const item: InfoItemDescriptor = {
        ...(publishedItems[index] ?? { key: field.key }),
        value: renderValue(field, values?.[field.key]),
      };
      (field.wide ? wide : short).push(item);
    });
    return { shortItems: short, wideItems: wide };
  }, [fields, publishedItems, values]);

  const resolvedTitle = resolveCardTitle(title ?? '', _layoutOverrides);
  const visibleActions = (actions ?? [])
    .map((action) => {
      const override = _layoutOverrides?.actions?.[action.key];
      return {
        ...action,
        label: override?.label ?? action.label,
        visible: override?.visible ?? true,
      };
    })
    .filter((action) => action.visible);

  return (
    <Card>
      {resolvedTitle !== '' && (
        <Card.Header>
          <CardTitleRow title={resolvedTitle} icon={icon} />
        </Card.Header>
      )}
      <Card.Content>
        <CardBodyState loading={loading} error={error} empty={values === null}>
          <VStack gap={4}>
            {shortItems.length > 0 && (
              <InfoList items={shortItems} columns={columns} layoutOverrides={_layoutOverrides} />
            )}
            {wideItems.length > 0 && (
              <InfoList items={wideItems} columns={1} layoutOverrides={_layoutOverrides} />
            )}
          </VStack>
        </CardBodyState>
      </Card.Content>
      {visibleActions.length > 0 && !loading && (
        <Card.Footer>
          <div className="flex flex-wrap justify-end gap-2">
            {visibleActions.map((action) => (
              <Button
                key={action.key}
                variant={action.variant ?? 'secondary'}
                size="sm"
                data-action-key={action.key}
                onClick={spaLink(action.to)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </Card.Footer>
      )}
    </Card>
  );
}
